/* global BigInt */

import { unlockVault } from './walletVault';

export const RELAY_URL = 'https://orkxz4jpah.execute-api.us-east-1.amazonaws.com/solana-relay';
export const ASSET_DECIMALS = { SOL: 9, THEO: 6, USDC: 6, USDT: 6 };

const ATA_PROGRAM = 'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL';

async function relay(body) {
  const res = await fetch(RELAY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!data.success) {
    const err = new Error(data.error || 'Relay error');
    err.signature = data.signature || null;
    throw err;
  }
  return data;
}

// "4.7" -> 4700000n (без float-помилок)
export function toBaseUnits(amount, decimals) {
  const s = String(amount).trim().replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(s)) throw new Error('Invalid amount');
  const [i, f = ''] = s.split('.');
  if (f.length > decimals) throw new Error(`Maximum ${decimals} decimal places`);
  const v = BigInt(i + f.padEnd(decimals, '0'));
  if (v <= 0n) throw new Error('Amount must be greater than 0');
  return v;
}

const u64LE = (v) => {
  const b = new Uint8Array(8);
  let x = v;
  for (let i = 0; i < 8; i++) { b[i] = Number(x & 0xffn); x >>= 8n; }
  return b;
};

// Крок 1: отримувач + чи треба створювати йому токен-акаунт
export const prepareSend = ({ asset, recipient, fromGenesisID, fromAddress }) =>
  relay({ action: 'prepare', asset, recipient, fromGenesisID, fromAddress });

// Будує і підписує транзакцію
export async function buildSignedTx(keypair, prep, amount) {
  const { PublicKey, Transaction, SystemProgram, TransactionInstruction } = await import('@solana/web3.js');

  const units = toBaseUnits(amount, prep.decimals);
  const owner = keypair.publicKey;
  const dest = new PublicKey(prep.recipientAddress);

  const tx = new Transaction({
    feePayer: owner,
    blockhash: prep.blockhash,
    lastValidBlockHeight: prep.lastValidBlockHeight,
  });

  if (prep.asset === 'SOL') {
    tx.add(SystemProgram.transfer({ fromPubkey: owner, toPubkey: dest, lamports: units }));
  } else {
    const mint = new PublicKey(prep.mint);
    const prog = new PublicKey(prep.tokenProgram); // THEO = Token-2022, USDC/USDT = Token
    const ataProg = new PublicKey(ATA_PROGRAM);
    const ata = (o) => PublicKey.findProgramAddressSync([o.toBuffer(), prog.toBuffer(), mint.toBuffer()], ataProg)[0];
    const src = ata(owner);
    const dst = ata(dest);

    if (prep.needsAccount) {
      tx.add(new TransactionInstruction({
        programId: ataProg,
        keys: [
          { pubkey: owner, isSigner: true, isWritable: true },
          { pubkey: dst, isSigner: false, isWritable: true },
          { pubkey: dest, isSigner: false, isWritable: false },
          { pubkey: mint, isSigner: false, isWritable: false },
          { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
          { pubkey: prog, isSigner: false, isWritable: false },
        ],
        data: Uint8Array.from([1]),
      }));
    }

    const data = new Uint8Array(10);
    data[0] = 12;
    data.set(u64LE(units), 1);
    data[9] = prep.decimals;
    tx.add(new TransactionInstruction({
      programId: prog,
      keys: [
        { pubkey: src, isSigner: false, isWritable: true },
        { pubkey: mint, isSigner: false, isWritable: false },
        { pubkey: dst, isSigner: false, isWritable: true },
        { pubkey: owner, isSigner: true, isWritable: false },
      ],
      data,
    }));
  }

  tx.sign(keypair);
  return btoa(String.fromCharCode(...tx.serialize()));
}

// Крок 2: пароль -> розшифрування ключа -> свіжий blockhash -> підпис -> відправка
export async function executeSend({ asset, recipient, amount, password, keypair: unlockedKeypair, fromGenesisID, fromAddress, expectedRecipientAddress }) {
  const keypair = unlockedKeypair || await unlockVault(fromAddress, password);
  if (keypair.publicKey.toBase58() !== fromAddress) throw new Error('Wallet key mismatch');

  const prep = await prepareSend({ asset, recipient, fromGenesisID, fromAddress });
  if (expectedRecipientAddress && prep.recipientAddress !== expectedRecipientAddress) {
    throw new Error('Recipient changed. Please start again.');
  }

  const signedTx = await buildSignedTx(keypair, prep, amount);
  const res = await relay({
    action: 'send',
    signedTx,
    blockhash: prep.blockhash,
    lastValidBlockHeight: prep.lastValidBlockHeight,
  });
  return { signature: res.signature, status: res.status, recipientAddress: prep.recipientAddress };
}